import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  Printer,
  Download,
  Mail,
  MessageCircle,
  FileWarning,
  History,
  AlertCircle,
  CalendarClock,
} from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/selectors';
import {
  fetchCentreList,
  fetchCentreIds,
  fetchDocumentPdf,
  fetchVersionPdf,
  errorMessage,
  openPendingWindow,
  showBlobIn,
} from '../../../api/document/documentCentreApi';
import SummaryCards from './components/SummaryCards';
import CentreFilters, { EMPTY_FILTERS } from './components/CentreFilters';
import DocumentTable, { EmptyTable } from './components/DocumentTable';
import DispatchReviewModal from './components/DispatchReviewModal';
import DocumentHistoryDrawer from './components/DocumentHistoryDrawer';
import { SECTIONS, canSendDocuments, plural } from './documentCentre.constants';
import styles from './DocumentCentre.module.css';

const PAYMENT_TYPES = ['payment_request', 'payment_reminder'];

/**
 * Documents centre (SUNSKY Documents spec v1.2).
 *
 * One overview for finding, checking and processing existing documents:
 * title, summary cards, filters, the table with checkboxes, the action bar
 * after a selection, the review window and the processing result (spec §3).
 * Documents are never generated or edited here; that stays in the booking
 * and Finance, which each row links to.
 */

const PAGE_SIZE = 25;

const SORTS = [
  { value: 'created:desc', label: 'Newest documents first' },
  { value: 'travelDate:asc', label: 'Travel date, soonest first' },
  { value: 'issueDate:desc', label: 'Document date, newest first' },
  { value: 'bookingReference:asc', label: 'Booking reference' },
];

const filtersFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return {
    ...EMPTY_FILTERS,
    q: params.get('q') || '',
    types: (params.get('types') || '').split(',').filter(Boolean),
    dispatchStatus: params.get('dispatchStatus') || '',
    release: params.get('release') || '',
    bookingStatus: params.get('bookingStatus') || '',
  };
};

const toParams = (filters) => ({ ...filters, includeHistorical: filters.includeHistorical ? 'true' : '' });

const DocumentCentre = ({ section = 'all' }) => {
  const meta = SECTIONS[section] || SECTIONS.all;
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const canSend = canSendDocuments(user);

  const initial = useMemo(() => filtersFromSearch(location.search), [location.search]);
  const [draft, setDraft] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(SORTS[0].value);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState(() => new Map());
  const [selectingAll, setSelectingAll] = useState(false);
  const [review, setReview] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const requestId = useRef(0);

  // A link with ?types= (old submenu URLs, cards) resets the view to it.
  useEffect(() => {
    setDraft(initial);
    setApplied(initial);
    setPage(1);
    setSelected(new Map());
  }, [initial]);

  useEffect(() => {
    const id = ++requestId.current;
    const [sortBy, sortDir] = sort.split(':');
    setLoading(true);
    setError('');
    fetchCentreList({ section, ...toParams(applied), page, pageSize: PAGE_SIZE, sortBy, sortDir })
      .then((res) => {
        if (id === requestId.current) setResult(res);
      })
      .catch(async (err) => {
        if (id !== requestId.current) return;
        const message = await errorMessage(err, 'Could not load the documents');
        setError(message);
        showToast(message, 'error');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [section, applied, page, sort, refreshKey, showToast]);

  const data = result?.data;
  const pagination = result?.meta || { page: 1, totalPages: 1, total: 0 };
  const rows = useMemo(() => data?.rows || [], [data]);
  const types = data?.types || [];
  const options = data?.options || {};
  const filteredCount = data?.counts?.documents ?? 0;

  // ── Filters ────────────────────────────────────────────────────────────
  // Changing filters clears the selection, with a notice (spec §5).
  const applyFilters = (next) => {
    setApplied(next);
    setPage(1);
    if (selected.size) {
      setSelected(new Map());
      showToast('Selection cleared because the filters changed', 'info');
    }
  };

  const onRemoveChip = (key) => {
    const next = { ...applied };
    if (key === 'date') Object.assign(next, { dateType: '', dateFrom: '', dateTo: '' });
    else if (key === 'types') next.types = [];
    else if (key === 'includeHistorical') next.includeHistorical = false;
    else next[key] = '';
    setDraft(next);
    applyFilters(next);
  };

  const setAndApply = (changes) => {
    const next = { ...applied, ...changes };
    setDraft(next);
    applyFilters(next);
  };
  // Payment requests and reminders get their own view of the Invoices page,
  // with their own cards (spec §4).
  const paymentView = applied.types.length > 0 && applied.types.every((t) => PAYMENT_TYPES.includes(t));
  const cardFilters =
    section === 'invoices'
      ? { overdue: () => setAndApply({ types: PAYMENT_TYPES }) }
      : {
          eligible_not_sent: () => setAndApply({ release: 'eligible', dispatchStatus: 'not_sent' }),
          blocked: () => setAndApply({ release: 'blocked', dispatchStatus: '' }),
          errors: () => setAndApply({ dispatchStatus: 'error', release: '' }),
        };

  // ── Selection (spec §11) ───────────────────────────────────────────────
  const selectable = rows.filter((r) => !r.historical);
  const onPage = selectable.filter((r) => selected.has(r.id)).length;
  const pageState = !selectable.length ? 'none' : onPage === selectable.length ? 'all' : onPage ? 'some' : 'none';
  const selectedBookings = new Set(selected.values()).size;
  const allFilteredSelected = selected.size > 0 && selected.size >= filteredCount;

  const toggleRow = (row) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row.bookingReference);
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageState === 'all') selectable.forEach((r) => next.delete(r.id));
      else selectable.forEach((r) => next.set(r.id, r.bookingReference));
      return next;
    });

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const res = await fetchCentreIds({ section, ...toParams(applied) });
      setSelected(new Map(res.items.map((item) => [item.id, item.bookingReference])));
      showToast(`${plural(res.documents, 'document')} from ${plural(res.bookings, 'booking')} selected`, 'success');
    } catch (err) {
      showToast(await errorMessage(err, 'Could not select all results'), 'error');
    } finally {
      setSelectingAll(false);
    }
  };

  // ── Row actions ────────────────────────────────────────────────────────
  const onView = async (row) => {
    const win = openPendingWindow();
    try {
      const blob = row.historical === 'version' ? await fetchVersionPdf(row.versionRowId) : await fetchDocumentPdf(row.id, 'view');
      showBlobIn(win, blob);
    } catch (err) {
      win?.close();
      showToast(await errorMessage(err, 'Could not open the document'), 'error');
    }
  };

  const onOpenBooking = (row) => {
    if (row.bookingId) navigate(`/bookings/${row.bookingId}`);
  };

  const openReview = (ids, channel) => setReview({ ids, channel, key: Date.now() });
  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleBlock}>
          <div className={styles.manager}>
            <FileText size={20} />
            {meta.title}
            <span className={styles.managerCount}>{filteredCount}</span>
          </div>
          <span className={styles.subtitle}>
            {meta.subtitle} Documents are generated and corrected in the booking or Finance, not here.
          </span>
        </div>
        <div className={styles.toolbarLinks}>
          {section === 'invoices' && (
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => setAndApply({ types: paymentView ? [] : PAYMENT_TYPES })}
            >
              <CalendarClock size={14} /> {paymentView ? 'All invoice documents' : 'Payment requests and reminders'}
            </button>
          )}
          {(section === 'all' || section === 'vouchers') && (
            <button type="button" className={styles.linkBtn} onClick={() => navigate('/documents/missing-travel-documents')}>
              <FileWarning size={14} /> Missing travel documents
            </button>
          )}
          <button type="button" className={styles.linkBtn} onClick={() => navigate('/documents/dispatch-history')}>
            <History size={14} /> Dispatch history
          </button>
        </div>
      </div>

      <SummaryCards cards={data?.cards} loading={loading} onCardFilter={cardFilters} />

      <CentreFilters
        section={section}
        draft={draft}
        setDraft={setDraft}
        applied={applied}
        types={types}
        options={options}
        loading={loading}
        onApply={() => applyFilters({ ...draft })}
        onClear={() => {
          setDraft(EMPTY_FILTERS);
          applyFilters(EMPTY_FILTERS);
        }}
        onRemoveChip={onRemoveChip}
      />

      {selected.size > 0 && (pageState === 'all' || allFilteredSelected) && filteredCount > 0 && (
        <div className={styles.selectionBanner}>
          {allFilteredSelected ? (
            <span>
              All {plural(selected.size, 'filtered document')} are selected ({plural(selectedBookings, 'booking')}). This set
              stays fixed: documents added later are not included.
            </span>
          ) : (
            <>
              <span>
                The {plural(onPage, 'document')} on this page {onPage === 1 ? 'is' : 'are'} selected.
              </span>
              {filteredCount > selected.size && (
                <button type="button" className={styles.bannerLink} onClick={selectAllFiltered} disabled={selectingAll}>
                  {selectingAll ? 'Selecting...' : `Select all ${filteredCount} filtered results`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          {error && !rows.length ? (
            <div className={styles.errorBox}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : loading && !rows.length ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              <span className={styles.loadingText}>Checking documents...</span>
            </div>
          ) : !rows.length ? (
            <EmptyTable filtered={JSON.stringify(applied) !== JSON.stringify(EMPTY_FILTERS)} />
          ) : (
            <DocumentTable
              rows={rows}
              selected={selected}
              onToggleRow={toggleRow}
              onTogglePage={togglePage}
              pageState={pageState}
              onView={onView}
              onProcess={(row, channel) => openReview([row.id], channel)}
              onHistory={(row) => setHistoryId(row.id)}
              onOpenBooking={onOpenBooking}
              canSend={canSend}
            />
          )}
        </div>
        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>
            Page {pagination.page} of {pagination.totalPages} ({plural(pagination.total, 'row')})
            {loading && rows.length ? ', updating...' : ''}
          </span>
          <div className={styles.paginationControls}>
            <select
              className={styles.select}
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              aria-label="Sort"
            >
              {SORTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
              let n;
              if (pagination.totalPages <= 5 || pagination.page <= 3) n = i + 1;
              else if (pagination.page >= pagination.totalPages - 2) n = pagination.totalPages - 4 + i;
              else n = pagination.page - 2 + i;
              return (
                <button
                  type="button"
                  key={n}
                  className={`${styles.pageBtn} ${n === pagination.page ? styles.pageBtnActive : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className={styles.actionBar} role="toolbar" aria-label="Selected documents">
          <span className={styles.actionCount}>
            {plural(selected.size, 'document')} <span>from {plural(selectedBookings, 'booking')}</span>
          </span>
          <button type="button" className={styles.actionBtn} onClick={() => openReview([...selected.keys()], 'print')}>
            <Printer size={14} /> Print
          </button>
          <button type="button" className={styles.actionBtn} onClick={() => openReview([...selected.keys()], 'download')}>
            <Download size={14} /> Download PDF
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.actionPrimary}`}
            onClick={() => openReview([...selected.keys()], 'email')}
            disabled={!canSend}
            title={canSend ? undefined : 'No permission to send documents'}
          >
            <Mail size={14} /> Email
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => openReview([...selected.keys()], 'whatsapp')}
            disabled={!canSend}
            title={canSend ? undefined : 'No permission to send documents'}
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
          <button type="button" className={styles.actionClear} onClick={() => setSelected(new Map())}>
            Clear selection
          </button>
        </div>
      )}

      {review && (
        <DispatchReviewModal
          key={review.key}
          open
          documentIds={review.ids}
          initialChannel={review.channel}
          onClose={() => setReview(null)}
          onFinished={refresh}
        />
      )}

      {historyId && (
        <DocumentHistoryDrawer
          documentId={historyId}
          onClose={() => setHistoryId(null)}
          onChanged={refresh}
          onOpenBooking={onOpenBooking}
        />
      )}
    </div>
  );
};

export default DocumentCentre;

// One component per submenu, so moving between submenus starts a fresh view
// (spec §2: submenus are the same overview with a preset document type).
export const AllDocuments = () => <DocumentCentre section="all" />;
export const InvoiceDocuments = () => <DocumentCentre section="invoices" />;
export const VoucherDocuments = () => <DocumentCentre section="vouchers" />;
export const BookingDocuments = () => <DocumentCentre section="booking" />;
